import { useTheme } from '@mui/material';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { filter, first, isNullish, map, pipe, prop, sortBy } from 'remeda';
import { TimeFormats, usePartnerHolidays } from 'src/graphql';
import { usePartnerBusinessHours } from 'src/graphql/entities/partner-business-hours';
import { ChatInfo } from 'src/pages/Conversations/Chat/ChatInfo/chat-info';
import { getPartnerHolidayMessage } from 'src/pages/Conversations/Chat/ChatInfo/partner-holiday-message';
import { DEFAULT_FROM } from 'src/pages/Settings/business-hours/const';
import { useDisplayTime } from 'src/utils/hooks';
import { useComposerBanners, useReportBannerVisible } from './composer-banners-context';

const MAX_DAYS_TO_SCAN_FOR_NEXT_BUSINESS_DAY = 7;
const HOLIDAY_START_ATTRIBUTE = 'holidayStart';
const HOLIDAY_END_ATTRIBUTE = 'holidayEnd';

interface HolidayBannerProps {
  partnerId: number;
}

export const HolidayBanner: React.FC<HolidayBannerProps> = ({ partnerId }) => {
  const theme = useTheme();
  const { timeFormatPref } = useDisplayTime();
  const { hasBannerBelow } = useComposerBanners();

  const { partnerBusinessHours, isLoaded: isBusinessHoursLoaded } = usePartnerBusinessHours({
    partnerId,
  });

  const filters = useMemo(() => {
    const today = dayjs().startOf('day');
    let nextDay = today.add(1, 'day');
    let nextDayTimeFrom = dayjs(DEFAULT_FROM).format('HH:mm');

    if (partnerBusinessHours.length > 0) {
      for (let i = 0; i < MAX_DAYS_TO_SCAN_FOR_NEXT_BUSINESS_DAY; i++) {
        if (partnerBusinessHours.find((it) => it.weekDay === nextDay.day())) {
          break; // Found a day with business hours
        }
        nextDay = nextDay.add(1, 'day');
      }

      nextDayTimeFrom = pipe(
        partnerBusinessHours,
        filter((it) => it.weekDay === nextDay.day()),
        map((it) => ({ ...it, sortField: dayjs(`0000-00-00T${it.timeFrom || ''}`) })),
        sortBy(prop('sortField')),
        first,
        // @ts-expect-error wrong type interference
        prop('timeFrom'),
      );
    }

    const [hours, minutes] = nextDayTimeFrom.split(':').map((part) => parseInt(part, 10));
    const nextWorkingDay = nextDay
      .startOf('day')
      .hour(hours)
      .minute(minutes || 0);

    return [
      {
        attribute: HOLIDAY_START_ATTRIBUTE,
        operator: '<=',
        value: nextWorkingDay.toISOString(),
      },
      {
        attribute: HOLIDAY_END_ATTRIBUTE,
        operator: '>',
        value: today.toISOString(),
      },
    ];
  }, [partnerBusinessHours]);

  const { partnerHolidays } = usePartnerHolidays(
    {
      partnerId,
      filters,
      limit: 1,
    },
    { skip: isNullish(partnerId) || !isBusinessHoursLoaded },
  );

  const hourCycle = timeFormatPref === TimeFormats.TwelveHour ? 'h12' : 'h24';

  // Priority (rose), not tertiary, so the holiday banner reads distinctly from the AI banner.
  const token = theme.palette.custom.OV.extended.Priority.color;

  const message = getPartnerHolidayMessage(partnerHolidays[0], token, hourCycle);

  useReportBannerVisible('holiday', !!message);

  if (!message) return null;

  return <ChatInfo message={message} token={token} bleed={hasBannerBelow('holiday')} />;
};
